import { Layouts } from '../../../controls/layouts';
import { Component, effect, inject, model, output, ChangeDetectionStrategy } from '@angular/core';
import { ErrorRuleCondition } from '../../models/error-rule-condition';
import { ControlContainer, NgForm, FormsModule } from '@angular/forms';
import { ConditionTypeProviderFn, ConditionValueProviderFn, ErrorRulesConditionComponent } from '../error-rules-condition/error-rules-condition.component';
import { ErrorRuleConditionType } from '../../models/error-rule-condition-type';
import { DeveloperService } from 'src/app/developer/services/developer.service';
import { ErrorRuleConditionValue } from '../../models/error-rule-condition-value';
import { ExceptionCategory } from 'src/app/developer/models/exception-category';
import { PartnerConfigurationKeyService } from 'src/app/partner/services/partner-configuration-key.service';
import { DataField, DataFieldTypes } from 'src/app/shared/datafields/models';
import { DistributionObjectService, DistributionService } from 'src/app/distribution/services';
import { ConditionTypes } from './condition-types';
import { DistributionObjectGroup } from 'src/app/distribution/models/distribution-object-group';
import { DocumentTypeEntity } from 'src/app/distribution/models';
import { TagsService } from 'src/app/tags/services/tags.service';
import { Tag, TagsUsage } from 'src/app/tags/models';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { LinkComponent } from '../../../controls/components/basic/link/link.component';

@Component({
    selector: 'app-error-rules-conditions',
    templateUrl: './error-rules-conditions.component.html',
    styleUrls: ['./error-rules-conditions.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [Layouts, FormsModule, ErrorRulesConditionComponent, LinkComponent]
})
export class ErrorRulesConditionsComponent {
  private developerService = inject(DeveloperService);
  private partnerConfigurationKeyService = inject(PartnerConfigurationKeyService); 
  private distributionService = inject(DistributionService);
  private distributionObjectService = inject(DistributionObjectService);
  private tagsService = inject(TagsService);
  private loadingPanelService = inject(LoadingPanelService);
  conditions = model.required<ErrorRuleCondition[]>();
  onSummaryChange = output<string>();
  
  conditionTypes: ErrorRuleConditionType[] = ConditionTypes;
  documentTypes: DocumentTypeEntity[];
  documentTypeGroups: DistributionObjectGroup[];
  exceptionCategories: ExceptionCategory[];
  partnerConfigurationKeys: DataField[];
  distributionConfigurationKeys: DataField[];
  partnerTags: Tag[];

  constructor() {
    effect(() => {
      var summary = "";

      const summarys = this.conditions().map(c => c.Summary);
      if (summarys.length > 0) {
        summary = summarys.join("\n\tand ");
        if (summary.trim() != "") {
          summary = "If " + summary;
        }
      }
      
      this.onSummaryChange.emit(summary);
    });
  }


  //** Change event handlers */
  summaryChange(summary: any, condition: ErrorRuleCondition) {
    this.conditions.update(c => {
      const nc = [...c];
      const idx = nc.findIndex(x => x.Id === condition.Id);
      nc[idx].Summary = summary;
      return nc;
    });
  }


  //** User initiated actions */
  addCondition() {
    this.conditions.update(c => [...c, { Id: this.getTempId(), TypeKey: null, ComparerKey: null, SubTypeKey: null, Values: [{ id: this.getTempId(), value: null }], Summary: null }]);
  }

  deleteCondition(idx: number) {
    this.conditions.update(c => {
      const newConditions = [...c];
      newConditions.splice(idx, 1)
      return newConditions;
    });
  }


  //** Condition type provider */
  getConditionTypes: ConditionTypeProviderFn = async (typeKey) => {
    if (typeKey) {
      if (typeKey === 'SenderPartnerConfiguration' || typeKey === 'ReceiverPartnerConfiguration' || typeKey === 'SenderReceiverPartnerConfiguration') {
        if (this.partnerConfigurationKeys == null) {
          this.partnerConfigurationKeys = await this.partnerConfigurationKeyService.getConfigurationKeys(0, "partner_general", null)
        }
  
        return this.partnerConfigurationKeys.map(c => {
          var comparers = [
            { key: "Equals", name: "equals" },
            { key: "NotEquals", name: "is not equal to" }
          ]
          if (c.DataType.DataTypeEnum == DataFieldTypes.TextBox) {
            comparers.push({ key: "Contains", name: "contains" });
          }
  
          return {
            key: c.DataFieldId.toString(),
            name: c.DisplayName,
            comparers: comparers,
            group: ""
          }
        });
      }
      else if (typeKey === 'DistributionConfiguration') {
        if (this.distributionConfigurationKeys == null) {
          this.distributionConfigurationKeys = await this.distributionService.getDistributionConfigurations("distribution_general")
        }
  
        return this.distributionConfigurationKeys.map(c => {
          var comparers = [
              { key: "Equals", name: "equals" },
              { key: "NotEquals", name: "is not equal to" }
          ]
          if (c.DataType.DataTypeEnum == DataFieldTypes.TextBox) {
              comparers.push({ key: "Contains", name: "contains" });
          }
  
          return {
            key: c.DataFieldId.toString(),
            name: c.DisplayName,
            comparers: comparers,
            group: ""
          }
        });
      }
      else {
        return [];
      }
    }
    else {
      return this.conditionTypes;
    }
  }


  //** Condition value provider */
  getConditionValues: ConditionValueProviderFn = async (typeKey, subTypeKey) => {
    if (typeKey === 'SenderPartnerConfiguration' || typeKey === 'ReceiverPartnerConfiguration' || typeKey === 'SenderReceiverPartnerConfiguration') {
      if (subTypeKey) {
        return this.getConfigurationKeyValues(subTypeKey);
      }
      else {
        return [];
      }
    }    
    else if (typeKey === 'DistributionConfiguration') {
      if (subTypeKey) {
        return this.getDistributionConfigurationKeyValues(subTypeKey);
      }
      else {
        return [];
      }
    }
    else if (typeKey === 'SenderPartnerTag' || typeKey === 'ReceiverPartnerTag' || typeKey === 'SenderReceiverPartnerTag') {
      return this.getPartnerTagValues();
    }
    else if (typeKey == 'ErrorCategory') {
      return this.getErrorCategoryValues();
    }
    else if (typeKey == 'DocumentType') {
      return this.getDocumentTypeValues();
    }
    else if (typeKey == 'DocumentTypeGroup') {
      return this.getDocumentTypeGroupValues();
    }
    else {
      return [];
    }
  }


  //** Helpers methods for condition value provider */
  async getDocumentTypeValues(): Promise<ErrorRuleConditionValue[]> {
    if (this.documentTypes == null) {
      try {
        this.loadingPanelService.showLoadingPanelDirect('Loading...');
        this.documentTypes = await this.distributionObjectService.getDistributionObjects();
      }
      finally {
        this.loadingPanelService.hideLoadingPanel();
      }
    }
    return this.documentTypes.map(d => {return {key: d.Id.toString(), name: d.Name};});
  }

  async getDocumentTypeGroupValues(): Promise<ErrorRuleConditionValue[]> {
    if (this.documentTypeGroups == null) {
      try {
        this.loadingPanelService.showLoadingPanelDirect('Loading...');
        this.documentTypeGroups = await this.distributionObjectService.getDistributionObjectGroups();
      }
      finally {
        this.loadingPanelService.hideLoadingPanel();
      }
    }
    return this.documentTypeGroups.map(d => {return {key: d.DistributionObjectGroupId.toString(), name: d.Name};});
  }

  async getErrorCategoryValues(): Promise<ErrorRuleConditionValue[]> {
    if (this.exceptionCategories == null) {
      try {
        this.loadingPanelService.showLoadingPanelDirect('Loading...');
        this.exceptionCategories = await this.developerService.getExceptionCategories();
      }
      finally {
        this.loadingPanelService.hideLoadingPanel();
      }
    }
    return this.exceptionCategories.map(d => {return {key: d.ExceptionCategoriesId.toString(), name: d.ExceptionCategoryName};});
  }

  async getConfigurationKeyValues(key: string): Promise<ErrorRuleConditionValue[]> {
    if (this.partnerConfigurationKeys == null) {
      try {
        this.loadingPanelService.showLoadingPanelDirect('Loading...');
        this.partnerConfigurationKeys = await this.partnerConfigurationKeyService.getConfigurationKeys(0, "partner_general", null)
      }
      finally {
        this.loadingPanelService.hideLoadingPanel();
      }
    }

    var confKey = this.partnerConfigurationKeys.find(c => c.DataFieldId.toString() == key)
    if (confKey) {
      if (confKey.Choices?.length > 0) {
        return confKey.Choices.map(c => {return {key: c.DataFieldChoiceId.toString(), name: c.DisplayValue};});
      }
      else if (confKey.DataType.DataTypeEnum == DataFieldTypes.CheckBox) {
        return [
          {key: 'true', name: 'True'},
          {key: 'false', name: 'False'},
        ];
      }
      else {
        return [];
      }
    }
    else {
      return [];
    }
  }

  async getDistributionConfigurationKeyValues(key: string): Promise<ErrorRuleConditionValue[]> {
    if (this.distributionConfigurationKeys == null) {
      try {
        this.loadingPanelService.showLoadingPanelDirect('Loading...');
        this.distributionConfigurationKeys = await this.distributionService.getDistributionConfigurations("distribution_general")
      }
      finally {
        this.loadingPanelService.hideLoadingPanel();
      }
    }

    var confKey = this.distributionConfigurationKeys.find(c => c.DataFieldId.toString() == key)
    if (confKey) {
      if (confKey.Choices?.length > 0) {
        return confKey.Choices.map(c => {return {key: c.DataFieldChoiceId.toString(), name: c.DisplayValue};});
      }
      else if (confKey.DataType.DataTypeEnum == DataFieldTypes.CheckBox) {
        return [
          {key: 'true', name: 'True'},
          {key: 'false', name: 'False'},
        ];
      }
      else {
        return [];
      }
    }
    else {
      return [];
    }
  }

  async getPartnerTagValues() {
    if (this.partnerTags == null) {
      try {
        this.loadingPanelService.showLoadingPanelDirect('Loading...');
        this.partnerTags = await this.tagsService.getTags(TagsUsage.Partner);
      }
      finally {
        this.loadingPanelService.hideLoadingPanel();
      }
    }
    return this.partnerTags.map(d => {return {key: d.Id.toString(), name: d.Name};});
  }


  //** Helper methods */
  getTempId(): string {
    return 'NEW_' + Math.floor((Math.random() * 100000) + 1);
  }
}
